import { styled } from "@mui/material/styles";
import React from "react";
import { ChevronRightIcon } from "./icons";
import { focusRing } from "./tokens";
export const Card = styled("div")({
    background: "var(--j-surface)",
    border: "1px solid var(--j-line)",
    borderRadius: "var(--j-r-md)",
    padding: "var(--j-sp-3)"
});
export const GroupedList = styled("div")({
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flexShrink: 0,
    margin: "0 calc(-1 * var(--j-sp-2))"
});
const bareIcon = {
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    "& > svg": {
        width: 16,
        height: 16,
        strokeWidth: 1.5
    }
};
export const Tile = styled("span", {
    shouldForwardProp: (prop)=>prop !== "tone"
})({
    ...bareIcon,
    color: "var(--j-ink-2)"
});
const RowRoot = styled("div")({
    ...focusRing,
    position: "relative",
    width: "100%",
    minHeight: "var(--j-row)",
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-2)",
    padding: "0 var(--j-sp-2)",
    border: 0,
    borderRadius: "var(--j-r-sm)",
    background: "transparent",
    color: "var(--j-ink)",
    textAlign: "left",
    fontSize: "var(--j-fs-md)",
    lineHeight: "var(--j-lh-md)",
    "&:has(> .row-main > .row-desc), &:has(> .row-main > .row-meta)": {
        minHeight: "2.75rem",
        padding: "var(--j-sp-1-5) var(--j-sp-2)",
        "& .row-title": {
            fontWeight: 500
        }
    },
    "button&, &[role=button], &[role=treeitem], &[role=option], &[data-interactive=true]": {
        cursor: "pointer",
        transition: "background-color var(--j-dur-fade)",
        "&:hover": {
            background: "var(--j-hover)"
        }
    },
    "&:focus-visible": {
        outlineOffset: "var(--j-focus-inset-offset)"
    },
    "&[aria-selected=true], &[data-selected=true]": {
        background: "var(--j-accent-soft)",
        color: "var(--j-accent-ink)",
        "&:hover": {
            background: "var(--j-accent-soft)"
        },
        "& > .row-icon, & .row-title, & .row-desc, & > .row-trailing, & > .row-chevron": {
            color: "var(--j-accent-ink)"
        }
    },
    "&[data-disabled=true]": {
        cursor: "default",
        "&:hover": {
            background: "transparent"
        },
        "& .row-title": {
            color: "var(--j-ink-2)"
        },
        "& > .row-icon": {
            color: "var(--j-ink-3)"
        },
        "& .row-desc": {
            whiteSpace: "normal"
        }
    },
    "& .row-stretch": {
        display: "block",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        padding: 0,
        border: 0,
        background: "transparent",
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        cursor: "pointer",
        outline: "none",
        "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit"
        },
        "&:focus-visible::after": {
            outline: "var(--j-focus-w) solid var(--j-accent)",
            outlineOffset: "var(--j-focus-inset-offset)"
        }
    },
    "& > .row-main": {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column"
    },
    "& .row-title": {
        fontWeight: 400,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
    },
    "& .row-desc": {
        fontSize: "var(--j-fs-sm)",
        lineHeight: "var(--j-lh-sm)",
        color: "var(--j-ink-2)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
    },
    "& .row-meta": {
        fontSize: "var(--j-fs-xs)",
        lineHeight: "var(--j-lh-xs)",
        color: "var(--j-ink-3)"
    },
    "& > .row-trailing": {
        position: "relative",
        zIndex: 1,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: "var(--j-sp-2)",
        color: "var(--j-ink-3)",
        fontVariantNumeric: "tabular-nums"
    },
    "& > .row-chevron": {
        color: "var(--j-ink-3)"
    }
});
export const ListRow = /*#__PURE__*/ React.forwardRef(function ListRow({ icon, iconTone = "primary", leading, title, description, meta, trailing, chevron = false, selected, disabled = false, dense = false, titleWeight, as, onClick, className, ...rest }, ref) {
    const element = as ?? (onClick ? "button" : "div");
    const buttonProps = element === "button" ? {
        type: "button",
        disabled
    } : {};
    return /*#__PURE__*/ React.createElement(RowRoot, {
        ref: ref,
        as: element,
        className: className ? `jc-row ${className}` : "jc-row",
        "data-flat": !icon,
        "data-dense": dense || undefined,
        "data-selected": selected || undefined,
        "data-disabled": disabled || undefined,
        "aria-disabled": disabled || undefined,
        onClick: disabled ? undefined : onClick,
        ...buttonProps,
        ...rest
    }, leading, icon && /*#__PURE__*/ React.createElement(Tile, {
        tone: iconTone,
        className: "row-icon"
    }, icon), /*#__PURE__*/ React.createElement("span", {
        className: "row-main"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "row-title",
        style: titleWeight ? {
            fontWeight: titleWeight
        } : undefined
    }, title), description && /*#__PURE__*/ React.createElement("span", {
        className: "row-desc"
    }, description), meta && /*#__PURE__*/ React.createElement("span", {
        className: "row-meta"
    }, meta)), trailing && /*#__PURE__*/ React.createElement("span", {
        className: "row-trailing"
    }, trailing), chevron && /*#__PURE__*/ React.createElement(ChevronRightIcon, {
        size: 14,
        className: "row-chevron"
    }));
});
export const SectionLabel = styled("div")({
    fontSize: "var(--j-fs-xs)",
    lineHeight: "var(--j-lh-xs)",
    fontWeight: 600,
    color: "var(--j-ink-3)",
    padding: "0 0 var(--j-sp-1)",
    margin: "var(--j-sp-1) 0 calc(-1 * var(--j-sp-3))"
});
export const Meta = styled("span")({
    fontSize: "var(--j-fs-xs)",
    lineHeight: "var(--j-lh-xs)",
    color: "var(--j-ink-3)"
});
export const Lede = styled("p")({
    fontSize: "var(--j-fs-md)",
    lineHeight: "var(--j-lh-md)",
    color: "var(--j-ink-2)",
    margin: 0
});
const TitleRoot = styled("div")({
    display: "flex",
    alignItems: "flex-start",
    gap: "var(--j-sp-2)",
    "& > .title-main": {
        flex: 1,
        minWidth: 0
    },
    "& h1": {
        fontSize: "var(--j-fs-xl)",
        lineHeight: "var(--j-lh-xl)",
        fontWeight: 600,
        letterSpacing: "-0.006em",
        margin: 0,
        outline: "none"
    },
    "& h1[data-size=md]": {
        fontSize: "var(--j-fs-2xl)",
        lineHeight: "var(--j-lh-2xl)"
    },
    "& p": {
        marginTop: "var(--j-sp-0-5)"
    }
});
export function PageTitle({ title, lede, action, size = "lg", titleRef, className }) {
    return /*#__PURE__*/ React.createElement(TitleRoot, {
        className: className
    }, /*#__PURE__*/ React.createElement("div", {
        className: "title-main"
    }, /*#__PURE__*/ React.createElement("h1", {
        ref: titleRef,
        "data-size": size,
        tabIndex: titleRef ? -1 : undefined
    }, title), lede && /*#__PURE__*/ React.createElement(Lede, null, lede)), action);
}
const EmptyRoot = styled("div")({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: "var(--j-sp-2)",
    padding: "var(--j-sp-8) var(--j-sp-6)",
    "& > .empty-title": {
        fontSize: "var(--j-fs-xl)",
        lineHeight: "var(--j-lh-xl)",
        fontWeight: 600,
        letterSpacing: "-0.006em"
    },
    "& > p": {
        maxWidth: "17rem"
    },
    "& > .empty-actions": {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "var(--j-sp-2)",
        marginTop: "var(--j-sp-2)"
    },
    "& > .empty-actions[data-stacked=true]": {
        flexDirection: "column",
        width: "100%",
        maxWidth: "15rem"
    }
});
const EmptyTile = styled("div", {
    shouldForwardProp: (prop)=>prop !== "tone"
})({
    ...bareIcon,
    color: "var(--j-ink-3)"
});
export function EmptyState({ icon, tone = "primary", title, text, actions, size = "md", stackActions = false, children, className }) {
    return /*#__PURE__*/ React.createElement(EmptyRoot, {
        className: className
    }, icon && /*#__PURE__*/ React.createElement(EmptyTile, {
        tone: tone
    }, icon), title && /*#__PURE__*/ React.createElement("div", {
        className: "empty-title",
        "data-size": size
    }, title), text && /*#__PURE__*/ React.createElement(Lede, null, text), children, actions && /*#__PURE__*/ React.createElement("div", {
        className: "empty-actions",
        "data-stacked": stackActions
    }, actions));
}
